import { Fragment, useRef, useState, type ReactNode } from "react";
import type {
  CatalogItemDto,
  CatalogPurchaseRequest,
  CatalogPurchaseResponse,
  HomeStageOfferDto,
  HomeUpgradeRequest,
  HomeUpgradeResponse,
} from "@townsy/contracts";
import { postCatalogPurchase, postHomeUpgrade } from "../../api/endpoints";
import { homeCommandAttempts, type CommandAttempt, type CommandFailure, type CommandKind } from "../../homes/commands";
import { ANCHOR_TYPE_SHORT, stageLabel, type MyHome } from "../../homes/homeLogic";
import { formatCoins } from "../../lib/wallet";
import { Dialog } from "../Dialog";
import { useTestPrices } from "../testPrices";

/**
 * Purchase reviews for Townsy Home Builders / Furnishings (docs/01 section 6 "Purchase review",
 * docs/04 sections 6 and 9). Same mechanics as the plot purchase: one confirmation = one
 * Idempotency-Key; "Try again" resends the SAME key and body, so a lost response can never spend
 * twice. A stale price/level ends the attempt; the visitor reviews the refreshed offer and confirms anew.
 */
function CoinReviewDialog<B, R>({
  title,
  testId,
  kind,
  target,
  body,
  price,
  coinsAvailable,
  rows,
  bullets,
  confirmLabel,
  onClose,
  onDone,
  onStale,
  send,
}: {
  title: string;
  testId: string;
  kind: CommandKind;
  target: string;
  body: B;
  price: number;
  coinsAvailable: number;
  rows: Array<[string, ReactNode]>;
  bullets: ReactNode[];
  confirmLabel: string;
  onClose(): void;
  onDone(response: R): void;
  onStale(): void;
  send(target: string, body: B, key: string): Promise<R>;
}) {
  const testPrices = useTestPrices();
  const attemptRef = useRef<CommandAttempt<B, R> | null>(null);
  const [state, setState] = useState<"review" | "submitting" | "failed">("review");
  const [failure, setFailure] = useState<CommandFailure | null>(null);

  const submit = async () => {
    // Created once, at the first confirmation; retries of this confirmation reuse its key and body.
    if (!attemptRef.current) attemptRef.current = homeCommandAttempts.forTarget<B, R>(kind, target, body);
    const attempt = attemptRef.current;
    setState("submitting");
    setFailure(null);
    const outcome = await attempt.submit(send);
    if (outcome.ok) {
      homeCommandAttempts.settle(attempt as CommandAttempt<unknown, unknown>);
      onDone(outcome.response);
      return;
    }
    setFailure(outcome);
    setState("failed");
    if (!outcome.retryable) {
      homeCommandAttempts.settle(attempt as CommandAttempt<unknown, unknown>);
      attemptRef.current = null;
      if (outcome.refresh) onStale();
    }
  };

  const submitting = state === "submitting";
  const canRetry = state === "failed" && failure?.retryable;
  const ended = state === "failed" && failure !== null && !failure.retryable;
  const summaryId = `${testId}-summary`;

  return (
    <Dialog
      title={title}
      onClose={() => {
        if (!submitting) onClose();
      }}
      describedBy={summaryId}
      footer={
        ended ? (
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Close
          </button>
        ) : (
          <>
            <button type="button" className="btn btn-primary" disabled={submitting} onClick={() => void submit()} data-testid={`${testId}-confirm`}>
              {submitting ? "Confirming…" : canRetry ? "Try again" : confirmLabel}
            </button>
            <button type="button" className="btn" disabled={submitting} onClick={onClose}>
              Cancel
            </button>
          </>
        )
      }
    >
      <div className="stack" id={summaryId} data-testid={testId}>
        <dl className="kv">
          {rows.map(([k, v]) => (
            <Fragment key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </Fragment>
          ))}
          <dt>Price</dt>
          <dd className="price">
            <strong>{formatCoins(price)}</strong>
          </dd>
          <dt>Paid with</dt>
          <dd>Coins (your coins: {formatCoins(coinsAvailable)})</dd>
          <dt>After purchase</dt>
          <dd>{formatCoins(Math.max(0, coinsAvailable - price))}</dd>
        </dl>
        <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
          {bullets.map((b, i) => (
            <li key={i}>{b}</li>
          ))}
          <li>Coin purchases are final: coins spent are not refunded{testPrices ? " (development test prices)" : ""}.</li>
          <li>Coins and dollar Credit are separate: this purchase uses coins only.</li>
        </ul>
        {failure ? (
          <div className={`notice ${failure.retryable ? "notice-warning" : "notice-danger"}`} role="alert" data-testid={`${testId}-error`}>
            {failure.message}
          </div>
        ) : null}
        {submitting ? (
          <p className="small muted" role="status">
            Confirming with Townsy…
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

export function ItemPurchaseDialog({
  item,
  coinsAvailable,
  onClose,
  onPurchased,
  onStale,
}: {
  item: CatalogItemDto;
  coinsAvailable: number;
  onClose(): void;
  onPurchased(response: CatalogPurchaseResponse): void;
  onStale(): void;
}) {
  const price = item.offer.coins ?? 0;
  const body: CatalogPurchaseRequest = { unit: "COIN", expectedPriceCoins: price };
  const fits = [...new Set(item.anchorTypes.map((t) => ANCHOR_TYPE_SHORT[t]))].join(" or ");
  return (
    <CoinReviewDialog<CatalogPurchaseRequest, CatalogPurchaseResponse>
      title={`Buy ${item.name}`}
      testId="item-purchase"
      kind="item"
      target={item.itemId}
      body={body}
      price={price}
      coinsAvailable={coinsAvailable}
      rows={[
        ["Item", <><strong>{item.name}</strong><div className="small muted">{item.description}</div></>],
        ["Fits", fits],
        ["You get", "One item, kept in your storage"],
      ]}
      bullets={[
        <>Place it from <strong>My home</strong> on a free {fits}. Placing, moving and removing are free.</>,
        "Items are never deleted: removing an item or resetting your layout puts it back in storage.",
      ]}
      confirmLabel={`Buy for ${formatCoins(price)}`}
      onClose={onClose}
      onDone={onPurchased}
      onStale={onStale}
      send={postCatalogPurchase}
    />
  );
}

export function UpgradeDialog({
  home,
  next,
  coinsAvailable,
  onClose,
  onUpgraded,
  onStale,
}: {
  home: Pick<MyHome, "propertyId" | "address" | "stage">;
  next: HomeStageOfferDto;
  coinsAvailable: number;
  onClose(): void;
  onUpgraded(response: HomeUpgradeResponse): void;
  onStale(): void;
}) {
  const price = next.offer?.coins ?? 0;
  const body: HomeUpgradeRequest = { toStage: next.stage, unit: "COIN", expectedPriceCoins: price };
  return (
    <CoinReviewDialog<HomeUpgradeRequest, HomeUpgradeResponse>
      title={`Upgrade to ${next.name}`}
      testId="upgrade"
      kind="upgrade"
      target={home.propertyId}
      body={body}
      price={price}
      coinsAvailable={coinsAvailable}
      rows={[
        ["Home", <strong key="a">{home.address}</strong>],
        ["Now", stageLabel(home.stage)],
        ["After", <strong key="n">{next.name}</strong>],
      ]}
      bullets={[
        "Upgrades go one level at a time and charge only the next level's price.",
        "Your yard items, furniture and look stay as they are.",
        <strong key="never">Homes can never be taken over by other players.</strong>,
      ]}
      confirmLabel={`Upgrade for ${formatCoins(price)}`}
      onClose={onClose}
      onDone={onUpgraded}
      onStale={onStale}
      send={postHomeUpgrade}
    />
  );
}
